import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Opportunity, SalesOpportunitiesSdk, SalesOpportunitiesService } from '@vendasta/sales-opportunities';
import { Observable, retry } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import {
  DurationInterface,
  DurationPeriod,
  SalesOrdersService,
  LineItem,
  RevenuePeriod,
  AttachmentInterface,
} from '@vendasta/sales-orders';
import { OpportunityToOrderLineItems } from '../../../../../sales-orders/conversion-utils';

import dayjs from 'dayjs';

export type CreateRedirectURLFn = (businessId: string, orderId: string) => string;

/**
 * A service that encapsulates the logic for creating an order from an opportunity
 * and redirecting the user.
 */
@Injectable({
  providedIn: 'root',
})
export class OpportunityOrderService {
  constructor(
    private readonly router: Router,
    private readonly salesOrdersService: SalesOrdersService,
    @Inject(SalesOpportunitiesService) private readonly opportunityApi: SalesOpportunitiesSdk,
  ) {}

  /**
   * Creates an order from an opportunity and redirects the user accordingly
   */
  createOrderAndRedirect(
    partnerId: string,
    marketId: string,
    opp: Opportunity,
    createRedirectURLFn: CreateRedirectURLFn,
  ): Observable<string> {
    const draftOrderId$ = this.createDraftOrder(partnerId, marketId, opp).pipe(shareReplay(1));
    return draftOrderId$.pipe(
      map((orderId: null | string) => {
        // If the feature is enabled and there is no order ID, the user canceled the action.
        if (!orderId) {
          return null;
        }
        const redirectURL = createRedirectURLFn(opp.accountGroupId, orderId);
        this.router.navigateByUrl(redirectURL);
        return orderId;
      }),
      take(1),
    );
  }

  private replaceEndDateWithDurationForLineItem(lineItem: LineItem): LineItem {
    if (lineItem.billingPeriod?.endDate) {
      const durationPeriod =
        lineItem.currentRevenue?.revenueComponents[0]?.period == RevenuePeriod.YEARLY
          ? DurationPeriod.YEAR
          : DurationPeriod.MONTH || DurationPeriod.MONTH;
      const computeFrequency = durationPeriod == DurationPeriod.YEAR ? 'year' : 'month';
      const durationValue = dayjs(lineItem.billingPeriod.endDate).diff(
        lineItem.billingPeriod.startDate,
        computeFrequency,
      );

      const d = {
        value: durationValue,
        duration: durationPeriod,
      } as any;

      lineItem.billingPeriod.duration = d;
    }
    return lineItem;
  }

  /**
   * Fetches the latest Opportunity data, then creates a draft order
   */
  private createDraftOrder(partnerId: string, marketId: string, opp: Opportunity): Observable<string> {
    // Must refetch the opportunity to get the latest data
    // TODO: update this component to properly store the latest opportunity data
    return this.opportunityApi
      .get(opp.opportunityId, opp.accountGroupId)
      .pipe(
        switchMap((opp) => {
          const lineItems = OpportunityToOrderLineItems(opp.lineItems);
          lineItems.map((li) => this.replaceEndDateWithDurationForLineItem(li));

          const duration = {
            value: opp.expectedContractDuration?.value || 1,
            duration: opp.expectedContractDuration?.duration || DurationPeriod.YEAR,
          } as DurationInterface;
          const attachments = opp.attachments?.map((a) => {
            return {
              name: a.name,
              url: a.url,
            } as AttachmentInterface;
          });
          return this.salesOrdersService.createDraft({
            partnerId: partnerId,
            marketId: marketId,
            businessId: opp.accountGroupId,
            salespersonId: opp.salesPersonId,
            lineItems: lineItems,
            requestedActivation: new Date(),
            contractDuration: duration,
            notes: opp.notes,
            tags: opp.tags,
            attachments: attachments,
            opportunityIds: [opp.opportunityId],
          });
        }),
      )
      .pipe(retry({ delay: 1000, count: 5 }));
  }
}
